import * as React from 'react';
import Button from '@mui/material/Button';
import { Avatar, Box, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Pagination, Select, Stack, TextField, Typography } from '@mui/material';
import { getPaginatedSports, paginateMatches, resyncDlqMatches, type DashboardMatchListSort } from '../Api';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid/DataGrid/DataGrid';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { GridCellParams } from '@mui/x-data-grid/models/params/gridCellParams';
import MatchInfoDto from '../models/dtos/MatchInfoDto';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import SportEntityDto from '../models/dtos/SportEntityDto';

type MatchesPageState = {
    before: Date;
    after: Date;
    loaded: boolean;
    currentPage: number;
    limit: number;
    totalPages: number;
    /** null = all match states */
    matchStateFilter: number | null;
    beforeDisplay: Date;
    afterDisplay: Date;
    searchTerm: string;
    matches: MatchInfoDto[];
    sportId: number | null;
    sort: DashboardMatchListSort;
    sports: SportEntityDto[];
    sportsLoaded: boolean;
};

export default function MatchesPage() {
    const defaultLimit = 25;
    let before = new Date();
    let after = new Date();
    before.setDate(before.getDate() + 7);
    after.setDate(after.getDate() - 7);

    const [state, setState] = React.useState<MatchesPageState>({
        before,
        after,
        loaded: false,
        currentPage: 0,
        limit: defaultLimit,
        totalPages: 0,
        matchStateFilter: null,
        beforeDisplay: before,
        afterDisplay: after,
        searchTerm: '',
        matches: [],
        sportId: null,
        sort: 'START_DATE_DESC',
        sports: [],
        sportsLoaded: false,
    });

    const stateRef = React.useRef(state);
    stateRef.current = state;

    const [providerSyncLoading, setProviderSyncLoading] = React.useState(false);
    const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const navigate = useNavigate();

    const onMatchClicked = async (matchId: number) => {
        navigate('/matches/' + matchId);
    };

    const getMatchStateText = (matchState: number) => {
        switch (matchState) {
            case 1:
                return 'Future';
            case 2:
                return 'Live';
            case 3:
                return 'Finished';
            case 5:
                return 'Cancelled';
            default:
                return 'Processing';
        }
    };

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'id', width: 128 },
        {
            field: 'sport.photo',
            headerName: 'Sport',
            width: 100,
            renderCell: (params: GridCellParams) => (
                <Avatar src={params.row.sport.photo_url} sx={{ width: 40, height: 40 }} />
            ),
        },
        {
            field: 'league.name',
            headerName: 'League',
            width: 150,
            renderCell: (params: GridCellParams) => <>{params.row.league?.name}</>,
        },
        {
            field: 'homeTeam.photo',
            headerName: 'Home',
            width: 64,
            renderCell: (params: GridCellParams) => (
                <Avatar src={params.row.home_team.photo_url} sx={{ width: 40, height: 40 }} />
            ),
        },
        {
            field: 'homeTeam.name',
            headerName: '',
            width: 200,
            renderCell: (params: GridCellParams) => <>{params.row.home_team?.name}</>,
        },
        {
            field: 'awayTeam.photo',
            headerName: 'Away',
            width: 64,
            renderCell: (params: GridCellParams) => (
                <Avatar src={params.row.away_team.photo_url} sx={{ width: 40, height: 40 }} />
            ),
        },
        {
            field: 'awayTeam.name',
            headerName: '',
            width: 200,
            renderCell: (params: GridCellParams) => <>{params.row.away_team?.name}</>,
        },
        {
            field: 'startDate',
            headerName: 'Date',
            width: 250,
            renderCell: (params: GridCellParams) => <>{new Date(params.row.date).toLocaleString()}</>,
        },
        {
            field: 'matchState',
            headerName: 'State',
            width: 250,
            renderCell: (params: GridCellParams) => <>{getMatchStateText(params.row.matchState)}</>,
        },
        {
            field: 'score',
            headerName: 'Score',
            width: 100,
            renderCell: (params: GridCellParams) => (
                <>
                    {params.row.score_home} - {params.row.score_away}
                </>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 250,
            renderCell: (params: GridCellParams) => (
                <Button variant="contained" fullWidth style={{ height: '32px' }} onClick={() => onMatchClicked(params.row.id)}>
                    Details
                </Button>
            ),
        },
    ];

    const loadPage = React.useCallback(async (page: number, patch?: Partial<MatchesPageState>) => {
        const s = { ...stateRef.current, ...patch };
        setState(prev => ({ ...prev, ...patch, loaded: false }));
        const response = await paginateMatches(page * s.limit, s.limit, s.before.toISOString(), s.after.toISOString(), {
            matchState: s.matchStateFilter,
            searchTerm: s.searchTerm.trim().length > 0 ? s.searchTerm.trim() : undefined,
            sportId: s.sportId,
            sort: s.sort,
        });
        const matches = response?.items ?? [];
        const totalItems = response?.totalItems ?? 0;
        const totalPages = Math.ceil(totalItems / s.limit);
        setState(prev => ({
            ...prev,
            ...patch,
            matches,
            loaded: true,
            totalPages,
            currentPage: page,
        }));
    }, []);

    React.useEffect(() => {
        loadPage(0);
    }, [loadPage]);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await getPaginatedSports(0, 500);
            if (cancelled || !res?.items) return;
            setState(prev => ({ ...prev, sports: res.items, sportsLoaded: true }));
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    React.useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    const onDateRangeChanged = async (newBefore: Date, newAfter: Date) => {
        setState(prev => ({ ...prev, before: newBefore, after: newAfter }));
        await loadPage(0, { before: newBefore, after: newAfter, currentPage: 0 });
    };

    const onSearchChange = (value: string) => {
        setState(prev => ({ ...prev, searchTerm: value }));
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            loadPage(0, { searchTerm: value, currentPage: 0 });
        }, 450);
    };

    const onPageChanged = async (_: React.ChangeEvent<unknown>, p: number) => {
        await loadPage(p - 1, { currentPage: p - 1 });
    };

    const resetClicked = async () => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
        const nb = new Date();
        const na = new Date();
        nb.setDate(nb.getDate() + 7);
        na.setDate(na.getDate() - 7);
        const patch: Partial<MatchesPageState> = {
            currentPage: 0,
            limit: 25,
            searchTerm: '',
            before: nb,
            after: na,
            beforeDisplay: nb,
            afterDisplay: na,
            sportId: null,
            sort: 'START_DATE_DESC',
            matchStateFilter: null,
        };
        setState(prev => ({ ...prev, ...patch }));
        await loadPage(0, patch);
    };

    const onResyncDlqClicked = async () => {
        const userConfirmed = window.confirm('Do you want to re-push DLQ matches from the source provider?');
        if (!userConfirmed) return;

        setProviderSyncLoading(true);
        try {
            let result = await resyncDlqMatches('Xbet');
            if (result === false) {
                setProviderSyncLoading(false);
                alert('Queue is already empty');
            } else if (!result) {
                throw new Error('Failed to resync DLQ matches');
            }
            setTimeout(() => {
                loadPage(stateRef.current.currentPage);
                setProviderSyncLoading(false);
            }, 10000);
        } catch (error) {
            setProviderSyncLoading(false);
            alert('Something went wrong');
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div>
                <Stack gap="50px 50px" sx={{ mx: 5, my: 5 }} justifyContent="center" alignItems="center">
                    <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 4, lg: 6 }} alignItems="center">
                        <Grid item xs={0} sm={1} md={1} lg={2} />
                        <Grid item xs={1} sm={1} md={1} lg={1}>
                            <Button variant="contained" fullWidth style={{ height: '54px' }} onClick={() => navigate('/top-matches')}>
                                TOP Matches
                            </Button>
                        </Grid>
                        <Grid item xs={1} sm={1} md={1} lg={1}>
                            <Button variant="contained" fullWidth style={{ height: '54px' }} onClick={() => navigate('/matches/preliminary-bet-results')}>
                                Preliminary bet results
                            </Button>
                        </Grid>
                        <Grid item xs={1} sm={1} md={1} lg={1}>
                            <Button variant="contained" fullWidth style={{ height: '54px' }} onClick={() => navigate('/recalculations')}>
                                Recalculations
                            </Button>
                        </Grid>
                        <Grid item xs={1} sm={1} md={1} lg={1}>
                            <Button
                                variant="outlined"
                                fullWidth
                                style={{ height: '54px', borderColor: 'blue', color: 'blue' }}
                                onClick={() => onResyncDlqClicked()}
                                disabled={providerSyncLoading}
                            >
                                {providerSyncLoading ? <CircularProgress size={24} /> : 'Sync DLQ Matches'}
                            </Button>
                        </Grid>
                        <Box width="100%" />
                        <Grid item xs={1} sm={1} md={1} lg={1}>
                            <Typography variant="h3">Matches</Typography>
                        </Grid>
                        <Box width="100%" />
                        <Grid item xs={1} sm={1} md={1} lg={2}>
                            <TextField
                                type="search"
                                label="Team or league search"
                                variant="filled"
                                fullWidth
                                value={state.searchTerm}
                                onChange={e => onSearchChange(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={1} sm={1} md={1} lg={1}>
                            <FormControl fullWidth variant="filled">
                                <InputLabel id="sport-filter-label">Sport</InputLabel>
                                <Select
                                    labelId="sport-filter-label"
                                    label="Sport"
                                    value={state.sportId === null ? '' : String(state.sportId)}
                                    disabled={!state.sportsLoaded}
                                    onChange={e => {
                                        const v = e.target.value;
                                        const sportId = v === '' ? null : Number(v);
                                        setState(prev => ({ ...prev, sportId }));
                                        loadPage(0, { sportId, currentPage: 0 });
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>All sports</em>
                                    </MenuItem>
                                    {state.sports.map(sp => (
                                        <MenuItem key={sp.id} value={String(sp.id)}>
                                            {sp.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={1} sm={1} md={1} lg={1}>
                            <FormControl fullWidth variant="filled">
                                <InputLabel id="sort-label">Sort</InputLabel>
                                <Select
                                    labelId="sort-label"
                                    label="Sort"
                                    value={state.sort}
                                    onChange={e => {
                                        const sort = e.target.value as DashboardMatchListSort;
                                        setState(prev => ({ ...prev, sort }));
                                        loadPage(0, { sort, currentPage: 0 });
                                    }}
                                >
                                    <MenuItem value="START_DATE_DESC">Date (newest first)</MenuItem>
                                    <MenuItem value="START_DATE_ASC">Date (oldest first)</MenuItem>
                                    <MenuItem value="LEAGUE_NAME_ASC">League (A–Z)</MenuItem>
                                    <MenuItem value="RELEVANCE">Search relevance</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={1} sm={1} md={1} lg={1}>
                            <FormControl fullWidth variant="filled">
                                <InputLabel id="state-label">Match state</InputLabel>
                                <Select
                                    labelId="state-label"
                                    label="Match state"
                                    value={state.matchStateFilter === null ? '' : String(state.matchStateFilter)}
                                    onChange={e => {
                                        const v = e.target.value;
                                        const matchStateFilter = v === '' ? null : Number(v);
                                        setState(prev => ({ ...prev, matchStateFilter }));
                                        loadPage(0, { matchStateFilter, currentPage: 0 });
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>All</em>
                                    </MenuItem>
                                    <MenuItem value="1">Future</MenuItem>
                                    <MenuItem value="2">Live</MenuItem>
                                    <MenuItem value="3">Finished</MenuItem>
                                    <MenuItem value="4">Processing</MenuItem>
                                    <MenuItem value="5">Cancelled</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={1} sm={1} md={1} lg={1}>
                            <DateTimePicker
                                label="After: "
                                value={dayjs(state.afterDisplay)}
                                onChange={e => setState(prev => ({ ...prev, afterDisplay: e!.toDate() }))}
                                slotProps={{ textField: { fullWidth: true } }}
                                onAccept={e => onDateRangeChanged(stateRef.current.before, e!.toDate())}
                            />
                        </Grid>
                        <Grid item xs={1} sm={1} md={1} lg={1}>
                            <DateTimePicker
                                label="Before: "
                                value={dayjs(state.beforeDisplay)}
                                onChange={e => setState(prev => ({ ...prev, beforeDisplay: e!.toDate() }))}
                                slotProps={{ textField: { fullWidth: true } }}
                                onAccept={e => onDateRangeChanged(e!.toDate(), stateRef.current.after)}
                            />
                        </Grid>
                        <Grid item xs={1} sm={1} md={1} lg={1}>
                            <Button variant="contained" fullWidth style={{ height: '54px' }} onClick={resetClicked}>
                                Reset
                            </Button>
                        </Grid>
                        <Box width="100%" />
                    </Grid>
                    {state.totalPages > 0 ? (
                        <Pagination count={state.totalPages} size="medium" onChange={onPageChanged} page={state.currentPage + 1} />
                    ) : null}
                    {!state.loaded ? (
                        <Grid item xs={2}>
                            <CircularProgress size="100px" style={{ marginBottom: '32px' }} />
                        </Grid>
                    ) : (
                        <DataGrid
                            sx={{ width: '100%' }}
                            rows={state.matches}
                            columns={columns}
                            getRowId={row => row.id}
                            initialState={{
                                pagination: { paginationModel: { page: 0, pageSize: 25 } },
                            }}
                            pageSizeOptions={[5, 10]}
                            hideFooterPagination
                        />
                    )}
                </Stack>
            </div>
        </LocalizationProvider>
    );
}
